import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Calendar, ArrowRightLeft, ImageOff } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { formatPrice } from "@/lib/currency";
import { cityFallbackImage } from "@/lib/cityImages";

function formatDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
        });
    } catch {
        return iso;
    }
}

const FALLBACK_IMG = "https://images.unsplash.com/photo-1616486029423-aaa4789e8c9a?crop=entropy&cs=srgb&fm=jpg&w=800&q=80";

export default function RoomCard({ room }) {
    const hasPhoto = Array.isArray(room.photos) && room.photos.length > 0;
    const cityFallback = cityFallbackImage(room.city);
    const img = hasPhoto ? fileUrl(room.photos[0]) : (cityFallback || FALLBACK_IMG);
    return (
        <Link
            to={`/rooms/${room.id}`}
            data-testid={`room-card-${room.id}`}
            className="group block bg-white border border-border/60 rounded-3xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl active:scale-[0.98]"
        >
            <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                <img
                    src={img}
                    alt={room.title}
                    loading="lazy"
                    decoding="async"
                    className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${hasPhoto ? "" : "opacity-40 grayscale contrast-75"}`}
                    onError={(e) => {
                        const el = e.currentTarget;
                        if (el.src !== FALLBACK_IMG) el.src = FALLBACK_IMG;
                    }}
                />
                {!hasPhoto && (
                    <div
                        data-testid={`room-card-nophoto-${room.id}`}
                        className="absolute inset-0 flex items-center justify-center"
                    >
                        <div className="bg-foreground/80 text-background px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.25em] inline-flex items-center gap-1.5 backdrop-blur-sm">
                            <ImageOff className="w-3.5 h-3.5" />
                            No photo
                        </div>
                    </div>
                )}
                {room.destination_city && (
                    <div className="absolute top-3 left-3 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                        <ArrowRightLeft className="w-3 h-3 text-primary" />
                        <span>going to {room.destination_city}</span>
                    </div>
                )}
                <div className="absolute bottom-3 right-3 bg-foreground text-background px-3 py-1 rounded-full text-sm font-semibold">
                    {formatPrice(room.rent_per_month, room.currency || "EUR", { perMonth: true })}
                </div>
            </div>
            <div className="p-5">
                <h3 className="font-display text-lg font-semibold line-clamp-1">{room.title}</h3>
                <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                    <MapPin className="w-3.5 h-3.5" />
                    <span>{room.city}, {room.country}</span>
                </div>
                <div className="flex items-center gap-1 text-sm text-muted-foreground mt-2">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>{formatDate(room.available_from)} - {formatDate(room.available_to)}</span>
                </div>
            </div>
        </Link>
    );
}
